import { EntityType } from "@prisma/client";
import { IconCheck, IconChecklist, IconChecks, IconCircleCheck, IconInbox, IconTargetArrow, IconUser } from "@tabler/icons-react";
import { useEffect, useMemo, useState } from "react";

import { DangerAlert } from "~/components/Alert";
import { useActionCenter } from "~/contexts/actionCenterProvider";
import { CreateTaskForm } from "~/modules/task/components/TaskForm";
import { Button, Flex, Skeleton, Text, tokens, UnstyledButton } from "~/ui";
import { api } from "~/utils/api";
import { InboxMessageRow } from "./InboxMessageRow";
import styles from "./InboxPanel.module.css";
import { getInboxEmptyState } from "./inbox-header-counts";
import { getInboxContextChipLabel } from "./inbox-task-row";

const INBOX_CLOCK_TICK_MS = 60_000;

interface InboxListQueryParams {
  includeCompleted?: boolean;
  entityId?: string;
  entityType?: EntityType;
  onlyMine?: boolean;
}

type ActionCenterInboxFiltersState = {
  onlyMine: boolean;
  showCompleted: boolean;
  onlyCurrentContext: boolean;
};

type ActionCenterInboxFiltersProps = {
  filters: ActionCenterInboxFiltersState;
  /** The current entity type when the context chip should show, else `null`. */
  currentContextEntityType: EntityType | null;
  onChange: (state: ActionCenterInboxFiltersState) => void;
};

type InboxFilterChipProps = {
  checked: boolean;
  icon: JSX.Element;
  label: string;
  onToggle: () => void;
};

/** A toggle pill (handoff §1): `aria-pressed` carries the state, the leading icon becomes a check when on. */
const InboxFilterChip = (props: InboxFilterChipProps): JSX.Element => (
  <UnstyledButton
    aria-pressed={props.checked}
    className={props.checked ? `${styles.chip} ${styles.chipOn}` : styles.chip}
    fw={500}
    fz="sm"
    onClick={props.onToggle}
  >
    {props.checked ? <IconCheck size={13} /> : props.icon}
    {props.label}
  </UnstyledButton>
);

const ActionCenterInboxFilters = (props: ActionCenterInboxFiltersProps): JSX.Element => {
  const handleUpdate = (payload: Partial<ActionCenterInboxFiltersState>) => {
    props.onChange({ ...props.filters, ...payload });
  };

  return (
    <div className={styles.chips}>
      <InboxFilterChip
        checked={props.filters.onlyMine}
        icon={<IconUser size={13} />}
        label="My tasks"
        onToggle={() => handleUpdate({ onlyMine: !props.filters.onlyMine })}
      />
      <InboxFilterChip
        checked={props.filters.showCompleted}
        icon={<IconCircleCheck size={13} />}
        label="Show completed"
        onToggle={() => handleUpdate({ showCompleted: !props.filters.showCompleted })}
      />
      {props.currentContextEntityType && (
        <InboxFilterChip
          checked={props.filters.onlyCurrentContext}
          icon={<IconTargetArrow size={13} />}
          label={getInboxContextChipLabel(props.currentContextEntityType)}
          onToggle={() => handleUpdate({ onlyCurrentContext: !props.filters.onlyCurrentContext })}
        />
      )}
    </div>
  );
};

// Fixed keys for the four placeholder rows (handoff §5); they never reorder.
const LOADING_ROW_KEYS = ["a", "b", "c", "d"];

const InboxLoadingRows = (): JSX.Element => (
  <div className={styles.list}>
    {LOADING_ROW_KEYS.map(key => (
      <div key={key} className={styles.skeletonRow}>
        <Skeleton circle height={32} />
        <Flex direction="column" gap={6}>
          <Skeleton height={10} width="70%" />
          <Skeleton height={8} width="45%" />
        </Flex>
      </div>
    ))}
  </div>
);

type ActionCenterTasksProps = {
  /** Held by the panel shell because the "New task" button lives in its header. */
  isCreating: boolean;
  onCreatingChange: (isCreating: boolean) => void;
};

export const ActionCenterTasks = (props: ActionCenterTasksProps): JSX.Element => {
  const utils = api.useContext();
  const actionCenterContext = useActionCenter();
  const usersQuery = api.users.searchUsersWithRoles.useQuery({});
  const countsQuery = api.inbox.counts.useQuery();
  // One clock for every row's relative time ("25m"); the panel body unmounts when the panel closes, so
  // this only ticks while the Inbox is open.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), INBOX_CLOCK_TICK_MS);
    return () => clearInterval(interval);
  }, []);
  const [filters, setFilters] = useState<ActionCenterInboxFiltersState>({
    onlyMine: true,
    showCompleted: false,
    onlyCurrentContext: false,
  });

  const showCurrentContextFilter = useMemo<boolean>(
    () => !!actionCenterContext.state.currentEntityType,
    [actionCenterContext.state.currentEntityType],
  );

  const inboxQueryParams = useMemo<InboxListQueryParams>(() => {
    const output: InboxListQueryParams = {
      includeCompleted: filters.showCompleted,
      onlyMine: filters.onlyMine,
    };
    if (showCurrentContextFilter && filters.onlyCurrentContext) {
      output.entityId = actionCenterContext.state.currentEntityId ?? undefined;
      output.entityType = actionCenterContext.state.currentEntityType ?? undefined;
    }
    return output;
  }, [filters, actionCenterContext.state, showCurrentContextFilter]);

  const inboxQuery = api.inbox.list.useQuery(inboxQueryParams);
  const markAllReadMutation = api.inbox.markAllRead.useMutation();

  const taskFormUsers = !!usersQuery.data
    ? usersQuery.data.map(x => ({
        id: x.id,
        name: `${x.firstName} ${x.lastName}`,
        email: x.emailAddress,
      }))
    : [];

  const refreshInbox = () => {
    void utils.inbox.list.invalidate();
    void utils.inbox.counts.invalidate();
  };

  const handleAfterCreate = async () => {
    refreshInbox();
    props.onCreatingChange(false);
  };

  const handleMarkAllRead = () => {
    markAllReadMutation.mutate(undefined, { onSuccess: refreshInbox });
  };

  const items = inboxQuery.data ?? [];
  const hasUnreadNotification = items.some(item => item.messageType === "NOTIFICATION" && !item.readAt);
  const emptyState = getInboxEmptyState(countsQuery.data?.unreadNotifications, countsQuery.data?.openTasks, inboxQuery.isError);

  return (
    <div className={styles.body}>
      <div className={styles.toolbar}>
        <ActionCenterInboxFilters
          filters={filters}
          currentContextEntityType={showCurrentContextFilter ? actionCenterContext.state.currentEntityType : null}
          onChange={setFilters}
        />
        {hasUnreadNotification && (
          <UnstyledButton className={styles.textButton} fw={500} fz="sm" onClick={handleMarkAllRead}>
            <IconChecks size={14} />
            Mark all read
          </UnstyledButton>
        )}
      </div>
      {inboxQuery.isPending && !inboxQuery.isRefetching ? (
        <InboxLoadingRows />
      ) : (
        <div className={styles.list}>
          {props.isCreating && (
            <div className={styles.formCard}>
              <Flex align="center" gap="sm" mb="sm">
                <IconChecklist size={16} />
                <Text c={tokens.color.content.strong} fw={600} fz="md" style={{ flex: 1 }}>
                  New task
                </Text>
                {actionCenterContext.state.currentEntityType && (
                  <Text c={tokens.color.content.secondary} fz="xs">
                    Linked to {getInboxContextChipLabel(actionCenterContext.state.currentEntityType).toLowerCase()}
                  </Text>
                )}
              </Flex>
              <CreateTaskForm
                entityId={actionCenterContext.state.currentEntityId}
                entityType={actionCenterContext.state.currentEntityType}
                users={taskFormUsers}
                onCancel={() => props.onCreatingChange(false)}
                onSuccess={handleAfterCreate}
              />
            </div>
          )}
          {items.map(item => (
            <InboxMessageRow
              key={item.messageType === "TASK" ? `task_${item.task.id}` : `notification_${item.id}`}
              item={item}
              now={now}
              onUpdate={refreshInbox}
              onOpened={refreshInbox}
            />
          ))}
          {items.length === 0 && inboxQuery.isError && (
            <Flex direction="column" p="md">
              <DangerAlert title="Could not load your Inbox">
                <Text size="sm">Check your connection and try again.</Text>
                <Button variant="light" size="xs" mt="sm" onClick={() => void inboxQuery.refetch()}>
                  Try again
                </Button>
              </DangerAlert>
            </Flex>
          )}
          {items.length === 0 && emptyState && (
            <div className={styles.empty}>
              <div className={styles.emptyIcon}>
                <IconInbox color={tokens.color.content.muted} size={24} />
              </div>
              <Text c={tokens.color.content.strong} fw={600} fz="md">
                {emptyState.title}
              </Text>
              <Text c={tokens.color.content.secondary} fz="sm" maw={260}>
                {emptyState.body}
              </Text>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
